/** @jsx jsx */
import {
  SessionOutputDisplayer,
  useAutoScrollConsoleToBottom,
} from '@datacamp/code-editor';
import { tokens } from '@datacamp/waffles/tokens';
import { jsx } from '@emotion/react';
import { Suspense, useEffect, useRef } from 'react';

import { useDispatch, useSelector } from '../../interfaces/State';
import * as actions from '../../redux/actions';
import * as selectors from '../../redux/selectors';
import DCSpinner from '../DCSpinner';
import ErrorBoundary from '../ErrorBoundary';
import Reconnecter from '../Reconnecter';

type Props = {
  uiTheme: 'LIGHT' | 'DARK';
};

export const OutputConsole: React.FC<Props> = ({ uiTheme }) => {
  const dispatch = useDispatch();
  const containerRef = useRef<HTMLDivElement>(null);
  const isSessionBroken = useSelector(selectors.isSessionBroken);
  const multiplexerOutputs = useSelector(selectors.selectOutput);
  const exerciseId = useSelector(selectors.selectExercise).get('id');
  const subExerciseId = useSelector(selectors.selectCurrentSubExercise).get(
    'id',
  );
  const currentExerciseId = subExerciseId != null ? subExerciseId : exerciseId;
  const outputTheme = uiTheme.toLowerCase() as 'light' | 'dark';

  useEffect(() => {
    dispatch(actions.clearConsoleOutput());
  }, [dispatch, currentExerciseId]);

  useEffect(() => {
    if (isSessionBroken) {
      dispatch(actions.clearConsoleOutput());
    }
  }, [dispatch, isSessionBroken]);

  useAutoScrollConsoleToBottom({
    divElementRef: containerRef,
    scrollWhenAnyValueChanges: [multiplexerOutputs],
  });

  return (
    <div
      css={{
        width: '100%',
        height: '100%',
        ...(uiTheme === 'DARK' && {
          backgroundColor: tokens.colors.navy,
        }),
      }}
    >
      <ErrorBoundary>
        <Suspense fallback={<DCSpinner uiTheme={uiTheme} />}>
          <div
            ref={containerRef}
            css={{
              height: '100%',
              fontFamily: tokens.fontFamilies.mono,
              fontSize: tokens.fontSizes.medium,
              padding: tokens.spacing.medium,
              overflowY: 'auto',
            }}
            data-cy="output-console"
          >
            {multiplexerOutputs.map((output, index) => (
              <SessionOutputDisplayer
                key={index}
                theme={outputTheme}
                sessionOutput={output}
              />
            ))}
            <Reconnecter />
          </div>
        </Suspense>
      </ErrorBoundary>
    </div>
  );
};
